import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { createUseStyles } from "react-jss";
import TextInput from "../inputs/TextInput";

const useStyles = createUseStyles({
  background: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100%",
    display: "flex",
    "align-items": "center",
    "justify-content": "center",
    opacity: props => (props.show ? 1 : 0),
    transition: "opacity 0.2s ease",
    "pointer-events": props => (props.show ? "auto" : "none"),
    "z-index": 100,
  },
  dim: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    "background-color": "rgba(0, 0, 0, 0.7)",
    "z-index": -1,
  },
  container: {
    width: "90%",
    "max-width": 700,
    "background-color": "#313131",
    "padding-bottom": 15,
    "border-radius": 5,
    "box-sizing": "border-box",
    "& h1": {
      color: "white",
    },
    "& > *": {
      padding: [0, 15],
    },
  },
  images: {
    width: "100%",
    height: "50vh",
    overflow: "auto",
    "box-sizing": "border-box",
    "margin-top": 15,
    padding: [20, 0, 0, 20],
    position: "relative",
  },
  image: {
    width: "calc(50% - 20px)",
    height: 150,
    "border-radius": 10,
    "background-color": "#252525",
    "background-size": "cover",
    position: "relative",
    cursor: "pointer",
    float: "left",
    margin: [0, 20, 20, 0],

    "& p": {
      color: "white",
      margin: 0,
      position: "absolute",
      right: 10,
      bottom: 5,
      "text-shadow": "0 0 1px #000000, 0 0 1px #000000, 0 0 1px #000000",
      textAlign: "right",
    },
  },
  message: {
    color: "white",
    margin: 0,
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
});

const ImageSelectorModal = forwardRef(({ onSubmit, images }, ref) => {
  const [show, setShow] = useState(false);
  const [availableImages, setAvailableImages] = useState([]);
  const [search, setSearch] = useState("");
  const classes = useStyles({ show });

  const imagesHash = images ? JSON.stringify(images) : null;

  useEffect(() => {
    setAvailableImages(Object.values(images || {}));
  }, [imagesHash]);

  const filteredImages = useMemo(() => {
    const arr = availableImages?.filter(x => !x.parent) || [];
    const searchKey = search?.toLowerCase();

    // Filter by search if provided
    let filtered = arr;
    if (searchKey) {
      filtered = arr.filter(
        x =>
          x.name?.toLowerCase()?.includes(searchKey) ||
          x.tag?.toLowerCase()?.includes(searchKey),
      );
    }

    // Deduplicate by image ID
    const seenIds = new Set();
    return filtered.filter(image => {
      if (seenIds.has(image.id)) {
        return false;
      }
      seenIds.add(image.id);
      return true;
    });
  }, [availableImages, search]);

  const onClose = useCallback(() => {
    setShow(false);
    setSearch("");
  }, []);

  useImperativeHandle(ref, () => ({
    show: data => {
      setShow(true);
      setSearch("");
      setAvailableImages(
        data !== undefined ? data : Object.values(images || {}),
      );
    },
    hide: () => setShow(false),
  }));

  return (
    <div className={classes.background}>
      <div className={classes.dim} onClick={onClose} />
      <div className={classes.container}>
        <h1>Image Selector</h1>
        <TextInput label="Search" value={search} onChange={s => setSearch(s)} />
        <div className={classes.images}>
          {filteredImages.length > 0 ? (
            filteredImages.map(image => (
              <div
                className={classes.image}
                key={image.id}
                style={{ backgroundImage: `url("${image.urls.low}")` }}
                onClick={() => {
                  setSearch("");
                  onSubmit(image);
                  setShow(false);
                }}
              >
                <p>{image.name}</p>
              </div>
            ))
          ) : (
            <p className={classes.message}>No images found</p>
          )}
        </div>
      </div>
    </div>
  );
});

export default ImageSelectorModal;
